import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useOffers, useCategories, useBrands } from "../api/hooks";
import { OfferCard } from "../components/OfferCard";
import { GridSkeleton, EmptyState, ErrorState } from "../components/States";
import { MaskedLine } from "../components/Motion";
import { Seo } from "../components/Seo";

const SORTS = [
    { value: "newest", label: "Newest first" },
    { value: "ending", label: "Ending soon" },
    { value: "savings", label: "Biggest savings" },
];

const TOGGLES = [
    { key: "verified", label: "Verified" },
    { key: "expiring", label: "Ending soon" },
    { key: "diversify", label: "One per brand" },
];

const selectCls =
    "rounded-full border border-ink/10 bg-white px-4 py-2.5 text-sm font-medium text-ink transition-colors focus:border-saffron focus:outline-none";

const OffersPage = ({ preset = {}, heading, eyebrow, description, seoTitle, seoDesc, path }) => {
    const [params] = useSearchParams();
    const [filters, setFilters] = useState({
        category: params.get("category") || "",
        brand: params.get("brand") || "",
        type: preset.type || "",
        verified: false,
        expiring: false,
        diversify: false,
        sort: "newest",
    });
    const [visible, setVisible] = useState(24);

    const { data: offers, isLoading, isError, refetch } = useOffers();
    const { data: categories } = useCategories();
    const { data: brands } = useBrands();

    const filtered = useMemo(() => {
        let list = Array.isArray(offers) ? [...offers] : [];
        if (filters.category) list = list.filter((o) => o.category === filters.category);
        if (filters.brand) list = list.filter((o) => o.brandId === filters.brand);
        if (filters.type) list = list.filter((o) => o.discountType === filters.type);
        if (filters.verified) list = list.filter((o) => o.isVerified);
        if (filters.expiring) list = list.filter((o) => o.expiringSoon);
        if (filters.diversify) {
            const seen = new Set();
            list = list.filter((o) => (seen.has(o.brandId) ? false : (seen.add(o.brandId), true)));
        }
        list.sort((a, b) => {
            if (filters.sort === "ending") return (a.daysLeft ?? 99999) - (b.daysLeft ?? 99999);
            if (filters.sort === "savings") return (b.discountValue || 0) - (a.discountValue || 0);
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });
        return list;
    }, [offers, filters]);

    const set = (key, value) => {
        setFilters((f) => ({ ...f, [key]: value }));
        setVisible(24);
    };

    const reset = () => {
        setFilters({ category: "", brand: "", type: preset.type || "", verified: false, expiring: false, diversify: false, sort: "newest" });
        setVisible(24);
    };

    return (
        <>
            <Seo title={seoTitle} description={seoDesc} path={path} />
            <section className="hero-glow bg-ink pb-14 pt-36 text-cream sm:pb-16 lg:pt-44">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                        <span className="h-px w-10 bg-saffron" />
                        {eyebrow}
                    </p>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl">
                        <MaskedLine delay={0.15}>{heading}</MaskedLine>
                    </h1>
                    {description && <p className="mt-5 max-w-xl text-base leading-relaxed text-mist">{description}</p>}
                    {offers && (
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-mist/70" data-testid="offers-count">
                            {filtered.length} live offer{filtered.length === 1 ? "" : "s"}
                        </p>
                    )}
                </div>
            </section>

            <section className="bg-cream py-12 sm:py-16" data-testid="offers-explorer">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <div className="flex flex-wrap items-center gap-3" data-testid="offers-filters">
                        <select value={filters.category} onChange={(e) => set("category", e.target.value)} className={selectCls} data-testid="filter-category" aria-label="Filter by category">
                            <option value="">All categories</option>
                            {(categories || []).map((c) => (
                                <option key={c.id} value={c.slug}>{c.name}</option>
                            ))}
                        </select>
                        <select value={filters.brand} onChange={(e) => set("brand", e.target.value)} className={selectCls} data-testid="filter-brand" aria-label="Filter by brand">
                            <option value="">All brands</option>
                            {(brands || []).map((b) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                        {!preset.type && (
                            <select value={filters.type} onChange={(e) => set("type", e.target.value)} className={selectCls} data-testid="filter-type" aria-label="Filter by type">
                                <option value="">All types</option>
                                <option value="code">Coupons</option>
                                <option value="deal">Deals</option>
                                <option value="percentage">% Off</option>
                            </select>
                        )}
                        <select value={filters.sort} onChange={(e) => set("sort", e.target.value)} className={selectCls} data-testid="filter-sort" aria-label="Sort offers">
                            {SORTS.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                        {TOGGLES.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => set(t.key, !filters[t.key])}
                                aria-pressed={filters[t.key]}
                                data-testid={`filter-${t.key}`}
                                className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${
                                    filters[t.key]
                                        ? "border-saffron bg-saffron text-white"
                                        : "border-ink/10 bg-white text-ink hover:border-saffron hover:text-saffron"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {isLoading && <GridSkeleton count={8} />}
                    {isError && <ErrorState onRetry={refetch} />}
                    {offers && filtered.length === 0 && (
                        <EmptyState title="No offers match these filters" message="Try removing a filter or two — new offers are added regularly.">
                            <button type="button" onClick={reset} data-testid="filters-reset" className="mt-6 rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream transition-colors hover:bg-india">
                                Reset filters
                            </button>
                        </EmptyState>
                    )}
                    {offers && filtered.length > 0 && (
                        <>
                            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="offers-grid">
                                {filtered.slice(0, visible).map((o) => (
                                    <OfferCard key={o.id} offer={o} />
                                ))}
                            </div>
                            {visible < filtered.length && (
                                <div className="mt-12 text-center">
                                    <button
                                        type="button"
                                        onClick={() => setVisible((v) => v + 24)}
                                        data-testid="offers-load-more"
                                        className="rounded-full border border-india px-8 py-4 text-sm font-medium text-india transition-colors hover:bg-india hover:text-white"
                                    >
                                        Load more ({filtered.length - visible} remaining)
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default OffersPage;
